
import { Component, OnInit, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { QuoteRequestState } from '@app/shared/models/quote-request-state';
import { UtilityHubIdentity } from '@auth/models/utility-hub-identity';
import { Quote } from '@quoting/models/quote';
import { QuotePriceHistoryModel } from '@quoting/models/quote-price-history';

enum QuoteDateTypeList {
  QuotePriceModifiedDate,
  QuoteContractStartDate
}

export interface IQuotePriceHistoryData {
  Term: number;
  QuotePriceCurrent: number;
  QuotePricePrevious: number;
  BrokerFeeCurrent: number;
  BrokerFeePrevious: number;
  BdmFeeCurrent: number;
  BdmFeePrevious: number;
  MarginFeeCurrent: number;
  MarginPrevious: number;  
}

@Component({
  selector: 'app-quote-history-dialog',
  templateUrl: './quote-history-dialog.component.html',
  styleUrls: ['./quote-history-dialog.component.scss'],
})

export class QuoteHistoryDialogComponent implements OnInit {

  public dataset: IQuotePriceHistoryData[] = [];


  quote: Quote;
  quoteRequestState: QuoteRequestState;  
  quotePriceHistory: QuotePriceHistoryModel[];
  quoteHistoryDataSource = new MatTableDataSource<IQuotePriceHistoryData>([]);
  quotePriceModifiedDateList: Date[];
  quotePriceContractStartDateList: Date[];
  selectedModifiedDate: Date;
  selectedContractStartDate: Date;
  isReadOnly: boolean = true; //Used for the radio buttons.
  isTaxIncluded: boolean;
  user: UtilityHubIdentity;
  quoteHistoryColumns: string[] = [ 'quoteTerm',
      'quotePriceCurrent',
      'quotePricePrevious',
      'brokerFeeCurrent',
      'brokerFeePrevious',
      'bdmFeeCurrent',
      'bdmFeePrevious',
      'marginCurrent',
      'marginPrevious'];

  constructor(
    public dialogRef: MatDialogRef<QuoteHistoryDialogComponent>,     
    @Inject(MAT_DIALOG_DATA) public data: any
    ) { }


  ngOnInit(): void {
    this.quoteRequestState = this.data.quote.quoteRequestState;
    this.quote = this.data.quote;
    this.user = this.data.user;
    if (this.user.isSupplierManager) {
    this.quoteHistoryColumns = [
      'quoteTerm',
      'quotePriceCurrent',
      'quotePricePrevious',
      'brokerFeeCurrent',
      'brokerFeePrevious',
      'bdmFeeCurrent',
      'bdmFeePrevious',
      'marginCurrent',
      'marginPrevious'
    ];} else { this.quoteHistoryColumns = [
      'quoteTerm',
      'quotePriceCurrent',
      'quotePricePrevious',
      'brokerFeeCurrent',
      'brokerFeePrevious',
      'bdmFeeCurrent',
      'bdmFeePrevious'
    ];}
  
    this.data.quotePriceHistory.subscribe(
      (data) => {
        this.isTaxIncluded = this.quote.isTaxIncluded;
        this.quotePriceHistory = data;
        this.quotePriceModifiedDateList = this.getQuoteDateListByDateType(QuoteDateTypeList.QuotePriceModifiedDate);
        this.quotePriceContractStartDateList = this.getQuoteDateListByDateType(QuoteDateTypeList.QuoteContractStartDate);
        this.selectedModifiedDate = this.getQuotePriceHistoryDefaultDate(this.quotePriceModifiedDateList);
        this.selectedContractStartDate = this.getQuotePriceHistoryDefaultDate(this.quotePriceContractStartDateList);
        this.quoteHistoryDataSource = new MatTableDataSource<IQuotePriceHistoryData>(
          this.getQuotePriceHistoryComparisonObject(this.getQuotePriceHistoryByDate(this.selectedModifiedDate)));
      },
      (err) => {
        this.dialogRef.close(err as Error);
      }
    );
  }

  getQuotePriceHistoryComparisonObject(quotePriceHistory: QuotePriceHistoryModel[]): IQuotePriceHistoryData[] {
    this.dataset = [];
    const currentPriceList = this.quote.prices;
    const historyPriceList = quotePriceHistory;

    for (const currentPrice of currentPriceList) {
      for (const historyPrice of historyPriceList) {
        if (currentPrice.term === historyPrice.term) {
          this.dataset.push({
            Term: currentPrice.term,
            QuotePriceCurrent: currentPrice.sellingPrice,
            QuotePricePrevious: historyPrice.sellingPrice,
            BrokerFeeCurrent: currentPrice.details[0]?.upliftPerUnit,
            BrokerFeePrevious: historyPrice.upliftDollar,
            BdmFeeCurrent: currentPrice.agentFee,
            BdmFeePrevious: historyPrice.agentFee,
            MarginFeeCurrent: currentPrice.margin,
            MarginPrevious: historyPrice.margin,
          });
        }
      }
    }

    return this.dataset;
  }

  getQuoteDateListByDateType(type: QuoteDateTypeList): Date[] {
    let dateList: any;
    switch(type) {
      case QuoteDateTypeList.QuotePriceModifiedDate:
        dateList = new Set(this.quotePriceHistory.map(obj => obj.quotePriceCreatedDate));
        break;
      case QuoteDateTypeList.QuoteContractStartDate:
        dateList = new Set(this.quotePriceHistory.map(obj => obj.contractStartDate));
        break;      
    }
    return Array.from(dateList);
  }

  getQuotePriceHistoryByDate(date: Date): QuotePriceHistoryModel[] {
    return this.quotePriceHistory.filter(obj => obj.quotePriceCreatedDate == date);
  }

  getQuotePriceHistoryDefaultDate(list: Date[]): Date {    
    const latestDate = list.reduce((maxDate, currentDate) => {
      return currentDate > maxDate ? currentDate : maxDate;
    }, list[0]);
    return latestDate;
  }

  quotePriceModifiedDateChanged(selected: Date): void {
    const selectedModifiedDate = this.getQuotePriceHistoryByDate(selected);
    const quotePriceHistoryData = this.getQuotePriceHistoryComparisonObject(selectedModifiedDate);
    this.updateQuotePriceHistoryData(quotePriceHistoryData);
  }

  updateQuotePriceHistoryData(data: IQuotePriceHistoryData[]): void {    
    this.quoteHistoryDataSource.data = data;
  }

}
